import { useEffect, useState } from "react";
import { useAccount, useChainId, useConnect, useSwitchChain } from "wagmi";
import { isAddress, type Address } from "viem";
import { REDES, porIdCadena, type RedKeel } from "./config/networks";
import { useVaults } from "./hooks/useVaults";
import type { VaultPublico } from "./hooks/useFlota";
import { ListaVaults } from "./components/ListaVaults";
import { CrearModal } from "./components/CrearModal";
import { ModalRescate } from "./components/ModalRescate";
import { DetalleVault } from "./components/DetalleVault";
import { Landing } from "./components/Landing";
import { Dashboard } from "./components/Dashboard";
import { Logo } from "./components/Logo";
import { Puente } from "./components/Puente";
import { Gobierno } from "./components/Gobierno";
import { MenuWallet } from "./components/MenuWallet";
import { Referidos, AvisoInvitacion } from "./components/Referidos";
import { Controles } from "./components/Controles";
import { useT } from "./lib/i18n";
import { SelectorRed } from "./components/SelectorRed";
import { IcoPuente, IcoReferidos, IcoGobierno } from "./components/Iconos";
import { useGobierno } from "./hooks/useGobierno";


type Vista = "vaults" | "puente" | "gobierno" | "referidos" | "landing" | "dashboard";
const VISTAS: Vista[] = ["vaults", "puente", "gobierno", "referidos", "landing", "dashboard"];

export default function App() {
  const t = useT();
  const { isConnected } = useAccount();
  const chainId = useChainId();
  const { connect, connectors, isPending } = useConnect();

  const { switchChain } = useSwitchChain();
  const vaults = useVaults();
  // Sección y vault abierto se RECUERDAN entre recargas. Antes vivían solo en memoria: al refrescar,
  // el navegador te devolvía a «vaults» aunque estuvieras en Puente o dentro de una piscina. Se
  // guardan en `localStorage` y se restauran al arrancar; validando la vista para no confiar en un
  // valor manipulado.
  const [abierto, setAbierto] = useState<Address | null>(
    () => (localStorage.getItem("keel:abierto") as Address | null) || null
  );
  const [manual, setManual] = useState("");
  const [vista, setVista] = useState<Vista>(() => {
    const v = localStorage.getItem("keel:vista");
    return v && VISTAS.includes(v as Vista) ? (v as Vista) : "vaults";
  });
  const [creando, setCreando] = useState(false);
  const [rescatando, setRescatando] = useState(false);
  // «Copiar» una piscina de la portada: `copia` es lo que se le pasa al modal ya abierto; `pendiente`
  // guarda la petición mientras hace falta conectar wallet o cambiar de red antes de poder abrirlo.
  const [copia, setCopia] = useState<{ pool: Address; pct: number } | null>(null);
  const [pendiente, setPendiente] = useState<{ red: RedKeel["clave"]; pool: Address; pct: number } | null>(null);

  useEffect(() => {
    localStorage.setItem("keel:vista", vista);
  }, [vista]);
  useEffect(() => {
    if (abierto) localStorage.setItem("keel:abierto", abierto);
    else localStorage.removeItem("keel:abierto");
  }, [abierto]);

  // `?mirar=0x…`: la cuenta de SOLO LECTURA de `wagmi.ts` se conecta sola. Sin esto habria que
  // pulsar «Conectar wallet», y en una captura no hay quien lo pulse. Solo existe en desarrollo.
  const espejo = connectors.find((c) => c.type === "mock");
  useEffect(() => {
    if (espejo && !isConnected) connect({ connector: espejo });
  }, [espejo, isConnected, connect]);

  const red = porIdCadena(chainId);
  const inyectada = connectors.find((c) => c.type === "injected") ?? connectors[0];

  // Copiar una piscina de la portada: saca el ±% de su rango actual y lanza la petición. Si hay que
  // conectar wallet o cambiar de red, `pendiente` la recuerda y el efecto de abajo la retoma cuando
  // ya se puede abrir el modal.
  const copiarPiscina = (v: VaultPublico) => {
    const pct = Math.max(0.1, Math.round((Math.sqrt(v.hi / v.lo) - 1) * 1000) / 10);
    setPendiente({ red: v.red, pool: v.pool, pct });
    if (!isConnected && inyectada) connect({ connector: inyectada });
  };
  useEffect(() => {
    if (!pendiente || !isConnected) return;
    const objetivo = REDES[pendiente.red].idCadena;
    if (chainId !== objetivo) {
      switchChain({ chainId: objetivo as never }); // hay que estar en la red del pool que se copia
      return;
    }
    setAbierto(null);
    setVista("vaults");
    setCopia({ pool: pendiente.pool, pct: pendiente.pct });
    setCreando(true);
    setPendiente(null);
  }, [pendiente, isConnected, chainId, connect, inyectada, switchChain]);

  // Puerta de DESARROLLO para poder mirar pantallas que normalmente exigen wallet. `import.meta.env.DEV`
  // es constante en el build de produccion, asi que esto se elimina entero al compilar: no viaja.
  const demo = import.meta.env.DEV ? new URLSearchParams(location.search).get("demo") : null;
  // `?barra=1` pinta la barra COMPLETA sin wallet. La barra es lo unico que se ve en todas las
  // pantallas y lo primero que se rompe al añadir una red —paso con la cuarta—, y hasta ahora no
  // habia forma de mirarla sin conectar. Solo en desarrollo: `import.meta.env.DEV` es constante
  // al compilar y esto no viaja a produccion.
  const barraDemo = import.meta.env.DEV && !!new URLSearchParams(location.search).get("barra");
  const conectado = isConnected || barraDemo;
  if (demo) {
    return (
      <div className="app">
        <main className="contenido">
          {demo === "crear" && <CrearModal abierto cerrar={() => {}} red={REDES.robinhood} />}
          {demo === "puente" && <Puente alVolver={() => {}} />}
          {demo === "dashboard" && <Dashboard alVolver={() => {}} />}
          {demo === "referidos" && <Referidos red={REDES.robinhood} alVolver={() => {}} />}
          {demo === "vault" && (
            <DetalleVault
              direccion={(new URLSearchParams(location.search).get("v") ?? "") as Address}
              red={REDES.robinhood}
              alVolver={() => {}}
            />
          )}
        </main>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="barra">
        {/* El logo lleva a la portada, conectado o no: es lo que hace todo el mundo cuando quiere
            volver al principio. Antes solo cerraba el detalle del vault y desde el resto de
            pantallas no hacia nada visible. */}
        <button className="marca" onClick={() => { setAbierto(null); setVista("landing"); }}
          title={t("Ir a la portada")}>
          <span className="logo"><Logo size={26} /></span>
          <span>Keellabs</span>
        </button>

        {/* LA RED, EN UN SOLO SITIO. Antes eran cuatro botones en fila y con la cuarta red la
            barra se partia en dos alturas. Ver la nota de SelectorRed. */}
        <SelectorRed
          chainId={chainId}
          conectado={conectado}
          alElegir={(id) => {
            setAbierto(null);
            // Elegir red desde la portada es querer entrar: si no, el clic no haria nada visible.
            if (vista === "landing") setVista("vaults");
            switchChain({ chainId: id as never });
          }}
        />

        {/* La navegacion va junta y a la derecha, separada de la red: son dos decisiones distintas
            —DONDE estas y QUE haces— y mezcladas en una fila se leian como siete botones iguales. */}
        {conectado && (
          <nav className="nav-barra" aria-label="Secciones">
            <button
              className={`enlace-barra ${vista === "vaults" && !abierto ? "activo" : ""}`}
              onClick={() => { setAbierto(null); setVista("vaults"); }}
              title="Tus piscinas"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 8c2 0 2 1.6 4 1.6S11 8 13 8s2 1.6 4 1.6S19 8 21 8" />
                <path d="M3 13c2 0 2 1.6 4 1.6S11 13 13 13s2 1.6 4 1.6S19 13 21 13" />
                <path d="M3 18c2 0 2 1.6 4 1.6S11 18 13 18s2 1.6 4 1.6S19 18 21 18" />
              </svg> <span>{t("Mis piscinas")}</span>
            </button>
            <button
              className={`enlace-barra ${vista === "dashboard" ? "activo" : ""}`}
              onClick={() => setVista(vista === "dashboard" ? "vaults" : "dashboard")}
              title={t("Estado del protocolo en tiempo real")}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
                <rect x="1.5" y="1.5" width="5.5" height="7" rx="1" /><rect x="9" y="1.5" width="5.5" height="4" rx="1" />
                <rect x="1.5" y="10.5" width="5.5" height="4" rx="1" /><rect x="9" y="7.5" width="5.5" height="7" rx="1" />
              </svg> <span>{t("Dashboard")}</span>
            </button>
            <button
              className={`enlace-barra ${vista === "puente" ? "activo" : ""}`}
              onClick={() => setVista(vista === "puente" ? "vaults" : "puente")}
              title={t("Mover dinero entre cadenas")}
            >
              <IcoPuente /> <span>{t("Bridge y Swap")}</span>
            </button>
            <button
              className={`enlace-barra ${vista === "referidos" ? "activo" : ""}`}
              onClick={() => setVista(vista === "referidos" ? "vaults" : "referidos")}
              title="Tu enlace: cobras parte de nuestra comision, y a quien invitas no le cuesta nada"
            >
              <IcoReferidos /> <span>{t("Referidos")}</span>
            </button>
            {/* Solo para quien gobierna de verdad esa red. Para cualquier otra wallet no existe: no
                es seguridad —el timelock ya rechaza a quien no manda— es no enseñar un boton que
                solo lleva a un revert. */}
            {red && <EnlaceGobierno red={red} vista={vista} setVista={setVista} />}
          </nav>
        )}

        {conectado ? (
          <MenuWallet />
        ) : (
          <>
            {/* Explorar piscinas: cualquiera puede ver todas las del protocolo sin conectar. Lleva
                al Dashboard, que es la visualización pública de la flota. */}
            <button
              className={`enlace-barra explorar ${vista === "dashboard" ? "activo" : ""}`}
              style={{ marginLeft: "auto" }}
              onClick={() => setVista(vista === "dashboard" ? "landing" : "dashboard")}
              title={t("Ver todas las piscinas del protocolo")}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="7" /><line x1="16.5" y1="16.5" x2="21" y2="21" />
              </svg>
              <span>{t("Explorar piscinas")}</span>
            </button>
            <button className="btn principal" onClick={() => inyectada && connect({ connector: inyectada })}
              disabled={!inyectada || isPending}>
              {isPending ? t("Conectando…") : t("Conectar wallet")}
            </button>
          </>
        )}

        {/* Tema (claro/oscuro) e idioma (ES/EN), al final de la barra en cualquier estado. */}
        <Controles />
      </header>

      <main className="contenido">
        {/* La invitación de referido se ofrece SIEMPRE que estás conectado y hay una pendiente, en
            CUALQUIER vista y arriba del todo: si solo saliera en «vaults», quien llega por un enlace
            y cae en otra pantalla nunca vería el botón de firmar. Se auto-oculta si no hay nada. */}
        {isConnected && <AvisoInvitacion red={red} />}

        {(!isConnected || vista === "landing") && vista !== "dashboard" && (
          <Landing
            yaConectado={isConnected}
            alConectar={() =>
              isConnected ? setVista("vaults") : inyectada && connect({ connector: inyectada })
            }
            conectando={isPending}
            alCopiar={copiarPiscina}
          />
        )}

        {vista === "dashboard" && (
          <Dashboard alVolver={() => setVista(isConnected ? "vaults" : "landing")} />
        )}

        {isConnected && !red && (
          <p className="aviso error">
            Estás en una red donde Keellabs no está desplegado. Elige otra en el selector de arriba.
          </p>
        )}


        {isConnected && vista === "puente" && <Puente alVolver={() => setVista("vaults")} />}

        {isConnected && red && vista === "referidos" && (
          <Referidos red={red} alVolver={() => setVista("vaults")} />
        )}

        {isConnected && red && vista === "gobierno" && (
          <Gobierno red={red} alVolver={() => setVista("vaults")} />
        )}

        {isConnected && red && abierto && vista === "vaults" && (
          <DetalleVault direccion={abierto} red={red} alVolver={() => setAbierto(null)} />
        )}

        {isConnected && red && !abierto && vista === "vaults" && (
          <>
            <div className="cabecera-lista">
              <div>
                <h1>{t("Tus inversiones")}</h1>
                <p className="sub" style={{ margin: "6px 0 0" }}>
                  {red.etiqueta} · el estable de esta red es {red.simboloEstable}
                </p>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {/* Quien llega con una posición que se le fue de rango no viene a invertir: viene a
                    recuperar. Es otra pregunta y otra pantalla, así que tiene su propio botón. */}
                <button className="btn" onClick={() => setRescatando(true)}>
                  {t("Rescatar una posición")}
                </button>
                <button className="btn principal" onClick={() => setCreando(true)}>
                  + Abrir una piscina
                </button>
              </div>
            </div>

            <ListaVaults consulta={vaults} red={red} alAbrir={setAbierto} />

            {/* Abrir un vault por direccion. Hace falta para los de generaciones anteriores, que
                pueden no salir en la lista de esta factory pero siguen teniendo dinero dentro. */}
            <details className="bloque manual">
              <summary>¿Tienes un vault que no sale en la lista?</summary>
              <div className="cuerpo">
                <p className="detalle">
                  Pega su dirección y se abre igual. Sirve para vaults de factories anteriores.
                </p>
                <div className="fila">
                  <input value={manual} onChange={(e) => setManual(e.target.value)} placeholder="0x…" />
                  <button className="btn" disabled={!isAddress(manual)}
                    onClick={() => setAbierto(manual as Address)}>Abrir</button>
                </div>
              </div>
            </details>

            <ModalRescate abierto={rescatando} cerrar={() => setRescatando(false)} red={red}
              alAbrirVault={(v) => { setRescatando(false); setAbierto(v); }} />

            <CrearModal abierto={creando} cerrar={() => { setCreando(false); setCopia(null); }} red={red}
              copiar={copia ?? undefined}
              alAbrirVault={(v) => { setCreando(false); setCopia(null); setAbierto(v); }} />
          </>
        )}
      </main>
    </div>
  );
}

/** El chip de gobierno: se pinta solo si esta wallet es la dueña del timelock de esta red. */
function EnlaceGobierno({
  red, vista, setVista,
}: {
  red: RedKeel;
  vista: string;
  setVista: (v: "vaults" | "puente" | "gobierno") => void;
}) {
  const t = useT();
  const { data } = useGobierno(red);
  if (!data?.puedoGobernar) return null;
  return (
    <button className={`enlace-barra ${vista === "gobierno" ? "activo" : ""}`}
      onClick={() => setVista(vista === "gobierno" ? "vaults" : "gobierno")}
      title={t("Publicar lógica nueva con el retardo del timelock")}>
      <IcoGobierno /> <span>{t("Gobierno")}</span>
    </button>
  );
}
